import { FunctionComponent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import styles from './forgottenPassword.module.scss'
import { CHANGE_PASSWORD } from '@/modules/API/software/passwordHandler'
import Loading from '@/components/Loading/Loading'
import LoggedOutNav from '@/components/Nav/LoggedOutNav/LoggedOutNav'
import Title from '@/components/_custom/Title/title'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonText from '@/components/_custom/Buttons/ButtonText/ButtonText'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import { GET_TOKEN } from '@/modules/API/software/getToken'

interface NewPasswordProps {
    token: string | null
}

const ChangePassword: FunctionComponent<NewPasswordProps> = ({ token }: NewPasswordProps) => {
    const { t } = useTranslation()

    const [newPassword, setNewPassword] = useState<string>('')
    const [newPasswordValid, setNewPasswordValid] = useState<boolean>(false)

    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('')
    const [confirmNewPasswordValid, setConfirmNewPasswordValid] = useState<boolean>(false)

    const [tokenPurpose, setTokenPurpose] = useState<string>('')

    const [passwordChanged, setPasswordChanged] = useState<boolean>(false)

    const [errorMessage, setErrorMessage] = useState<string>('')

    const [changePassword] = useMutation(CHANGE_PASSWORD, {
        variables: {
            password: newPassword,
            token: token
        },
        onCompleted: () => {
            setPasswordChanged(true)
            setErrorMessage('')
        },
        onError: () => {
            setErrorMessage(t('generalError'))
        }
    })

    const { loading: loadingToken, data: dataToken } = useQuery(GET_TOKEN, {
        variables: {
            token: token
        }
    })

    useEffect(() => {
        if (dataToken) {
            setTokenPurpose(dataToken.getToken)
        }
    }, [dataToken])

    async function handleChangePassword(): Promise<void> {
        await changePassword()
    }

    return (
        <div className={styles.PagePosition}>
            {loadingToken && <Loading overall />}
            <LoggedOutNav />
            <div className={styles.PasswordPlacement}>
                {tokenPurpose === 'password_reset' ? (
                    <div className={styles.Form}>
                        <div className={styles.Title}>
                            <Title text={t('changePassword')} />
                        </div>
                        {!passwordChanged ? (
                            <>
                                <div className={styles.Input}>
                                    <UserInput
                                        id='mdp'
                                        label={t('password')}
                                        type={'password'}
                                        verify={'password'}
                                        required
                                        fullWidth
                                        value={newPassword}
                                        setValue={setNewPassword}
                                        setValid={setNewPasswordValid}
                                    />
                                    <UserInput
                                        id='confirmMdp'
                                        label={t('confirmPassword')}
                                        type={'password'}
                                        verify={'confirmPassword'}
                                        confirmPasswordValue={newPassword}
                                        required
                                        fullWidth
                                        className={styles.InputField}
                                        value={confirmNewPassword}
                                        setValue={setConfirmNewPassword}
                                        setValid={setConfirmNewPasswordValid}
                                    />
                                </div>
                                {errorMessage && <ErrorMessage error={errorMessage} className={styles.ErrorPosition} />}
                                <ButtonText
                                    text={t('confirm')}
                                    onClick={handleChangePassword}
                                    fullWidth
                                    disabled={!newPasswordValid || !confirmNewPasswordValid}
                                />
                                <Link to='/' className={styles.Link}>
                                    {t('returnToLogin')}
                                </Link>
                            </>
                        ) : (
                            <div className={styles.ValidationText}>
                                <p className={styles.Text}>{t('passwordChanged')}</p>
                                <Link to='/' className={styles.Link}>
                                    <ButtonText text={t('returnToLogin')} onClick={() => {}} fullWidth />
                                </Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={styles.Form}>
                        <div className={styles.Title}>
                            <Title text={t('invalidToken')} />
                        </div>
                        <div className={styles.ValidationText}>
                            <Link to='/' className={styles.Link}>
                                <ButtonText text={t('returnToLogin')} onClick={() => {}} fullWidth />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ChangePassword
